/**
 * Help Section Component
 *
 * Individual help section with icon, title, description, and optional CTA link.
 * Used within HelpPanel to display contextual help information.
 */

import React, { type ReactNode, useState } from 'react';
import { ArrowRight, type LucideIcon, ChevronDown } from 'lucide-react';

export interface HelpSectionProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  children?: ReactNode;
  cta?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  variant?: 'default' | 'primary' | 'success' | 'warning';
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  className?: string;
}

export const HelpSection: React.FC<HelpSectionProps> = ({
  icon: Icon,
  title,
  description,
  children,
  cta,
  variant = 'default',
  collapsible = false,
  defaultCollapsed = false,
  className = '',
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const variantStyles = {
    default: {
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      ctaColor: 'text-primary-600 hover:text-primary-700',
    },
    primary: {
      iconBg: 'bg-primary-100',
      iconColor: 'text-primary-600',
      ctaColor: 'text-primary-600 hover:text-primary-700',
    },
    success: {
      iconBg: 'bg-success-100',
      iconColor: 'text-success-600',
      ctaColor: 'text-success-600 hover:text-success-700',
    },
    warning: {
      iconBg: 'bg-warning-100',
      iconColor: 'text-warning-600',
      ctaColor: 'text-warning-600 hover:text-warning-700',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className={`help-section ${className}`}>
      {/* Header */}
      <div
        className={`flex items-start gap-3 mb-2 ${collapsible ? 'cursor-pointer' : ''}`}
        onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
      >
        {Icon && (
          <div
            className={`flex-shrink-0 w-10 h-10 rounded-lg ${styles.iconBg} flex items-center justify-center`}
          >
            <Icon className={`w-5 h-5 ${styles.iconColor}`} />
          </div>
        )}
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
        </div>
        {collapsible && (
          <button
            type="button"
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
          >
            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform ${
                isCollapsed ? '-rotate-90' : 'rotate-0'
              }`}
            />
          </button>
        )}
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <>
          {/* Description */}
          {description && (
            <p className="text-sm text-gray-600 leading-relaxed mb-3 ml-13">
              {description}
            </p>
          )}

          {/* Custom Content */}
          {children && <div className="text-sm text-gray-600 mb-3 ml-13">{children}</div>}

          {/* CTA Link */}
          {cta && (
            <div className="ml-13">
              {cta.href ? (
                <a
                  href={cta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1 text-sm font-medium ${styles.ctaColor} transition-colors`}
                >
                  {cta.label}
                  <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <button
                  onClick={cta.onClick}
                  className={`inline-flex items-center gap-1 text-sm font-medium ${styles.ctaColor} transition-colors`}
                >
                  {cta.label}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
